import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import { useDropzone } from 'react-dropzone'
import { downloadInvoicePdf, printInvoicePdf } from '../../utils/invoicePdf'
import * as api from '../../api/index'
import { useRhGuard } from '../../hooks/useRoleGuard'
import { isAdmin } from '../../utils/auth'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import BackButton from '../BackButton/BackButton'
import Spinner from '../Spinner/Spinner'
import { formatTND } from '../../utils/formatTND'
import { COACH_TITRE, normalizeCoachTitre } from '../../utils/coachTitre'
import {
  FISCAL_CATEGORY_OPTIONS,
  formatFiscalCategoryLabel,
  isAutoEntrepreneur,
} from '../../utils/fiscalCategory'
import {
  RETENUE_OPTIONS,
  applyFiscalCategoryToForm,
  formatRetenueLabel,
  normalizeCoachRetenue,
} from '../../utils/retenue'
import styles from './Coaches.module.css'

const MONTHS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

const formatPeriod = (month, year) => {
  if (!month || !year) return '—'
  return `${MONTHS[month]} ${year}`
}

const DOC_TYPE_OPTIONS = [
  { value: '', label: 'Détection auto (nom du fichier)' },
  { value: 'patente', label: 'Patente' },
  { value: 'cin', label: 'CIN' },
  { value: 'rib', label: 'RIB' },
  { value: 'contrat', label: 'Contrat' },
  { value: 'attestation', label: 'Attestation' },
  { value: 'autre', label: 'Autre' },
]

const formatFileSize = (bytes) => {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`
}

const formatDocDate = (value) => {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const statusBadge = (status) => {
  if (status === 'sent') return { cls: styles.badgeOk, text: 'Envoyée' }
  if (status === 'failed') return { cls: styles.badgeErr, text: 'Échec' }
  return { cls: styles.badgePending, text: 'En attente' }
}

const coachToForm = (coach) => ({
  name: coach?.name || '',
  matriculeFiscal: coach?.matriculeFiscal || '',
  address: coach?.address || '',
  email: coach?.email || '',
  phone: coach?.phone || '',
  rib: coach?.rib || '',
  idSage: coach?.idSage || '',
  titre: normalizeCoachTitre(coach?.titre),
  stampImage: coach?.stampImage || '',
  isActive: coach?.isActive !== false,
  fiscalCategory: coach?.fiscalCategory === 'regime_reel' || coach?.fiscalCategory === 'regime_forfaitaire'
    ? coach.fiscalCategory
    : 'auto_entrepreneur',
  retenue: normalizeCoachRetenue(coach?.retenue, coach?.fiscalCategory),
})

const parseCoachDisplayName = (name = '') =>
  name.trim().replace(/^(MME|M\.|MR|MONSIEUR|MADAME)\s*/i, '').trim()

const VirtualStampPreview = ({ name, matriculeFiscal, address }) => {
  const displayName = parseCoachDisplayName(name).toUpperCase() || 'NOM PRÉNOM'
  return (
    <div className={styles.stampPreviewVirtual} aria-hidden="true">
      <div className={styles.stampPreviewName}>{displayName}</div>
      <div className={styles.stampPreviewRole}>((Coach Sportif))</div>
      {address?.trim() && <div className={styles.stampPreviewAddress}>{address.trim()}</div>}
      <div className={styles.stampPreviewMf}>MF: {matriculeFiscal || '—'}</div>
    </div>
  )
}

const StampUploader = ({ form, setForm }) => {
  const [uploading, setUploading] = useState(false)

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0]
    if (!file) return

    const url = 'https://api.cloudinary.com/v1_1/almpo/image/upload'
    const formData = new FormData()
    formData.append('file', file)
    formData.append('upload_preset', 'invoice')
    setUploading(true)

    fetch(url, { method: 'post', body: formData })
      .then((response) => response.json())
      .then((data) => {
        if (data.secure_url) {
          setForm((prev) => ({ ...prev, stampImage: data.secure_url }))
        }
      })
      .catch(() => {})
      .finally(() => setUploading(false))
  }, [setForm])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/*',
    multiple: false,
    disabled: uploading,
  })

  return (
    <div className={styles.field}>
      <label>Cachet / signature</label>
      <p className={styles.stampHint}>
        Sans image personnalisée, un cachet virtuel est généré automatiquement sur la facture.
      </p>

      <div className={styles.stampPreviewWrap}>
        <div className={styles.stampBlock}>
          <p className={styles.stampPreviewLabel}>Signature et Cachet</p>
          {form.stampImage ? (
            <img src={form.stampImage} alt="Cachet personnalisé" className={styles.stampPreviewImage} />
          ) : (
            <VirtualStampPreview name={form.name} matriculeFiscal={form.matriculeFiscal} address={form.address} />
          )}
        </div>
      </div>

      <div
        {...getRootProps()}
        className={`${styles.stampUploadZone} ${isDragActive ? styles.stampUploadActive : ''}`}
      >
        <input {...getInputProps()} />
        <span className={styles.stampUploadIcon}>{isDragActive ? '📥' : '🖼️'}</span>
        <strong>{uploading ? 'Envoi en cours…' : 'Glisser une image ou cliquer pour importer un cachet'}</strong>
        <span className={styles.stampUploadHint}>PNG, JPG — fond transparent recommandé</span>
      </div>

      {form.stampImage && (
        <button
          type="button"
          className={styles.stampClearBtn}
          onClick={() => setForm((prev) => ({ ...prev, stampImage: '' }))}
          disabled={uploading}
        >
          Utiliser le cachet virtuel
        </button>
      )}
    </div>
  )
}

const PAGE_SIZE_OPTIONS = [10, 20, 50]

const CoachDetail = () => {
  const { id } = useParams()
  const history = useHistory()
  const location = useLocation()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useRhGuard()
  const admin = isAdmin()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [form, setForm] = useState(coachToForm(null))
  const [saving, setSaving] = useState(false)
  const [uploadingDocs, setUploadingDocs] = useState(false)
  const [docTypeUpload, setDocTypeUpload] = useState('')
  const [uploadDocError, setUploadDocError] = useState('')
  const [deletingDocId, setDeletingDocId] = useState(null)
  const [pdfBusyInvoiceId, setPdfBusyInvoiceId] = useState(null)

  const goBackToCoaches = useCallback(() => {
    const returnQuery = location.state?.coachesListQuery
    if (typeof returnQuery === 'string') {
      history.push(`/coaches${returnQuery}`)
      return
    }
    if (window.history.length > 1) {
      history.goBack()
      return
    }
    history.push('/coaches')
  }, [history, location.state])

  const loadCoach = useCallback(async (options = {}) => {
    const { keepExisting = false } = options
    if (!keepExisting) setLoading(true)
    try {
      const { data: result } = await api.fetchCoach(id, { page, limit: pageSize })
      setData(result)
      setUploadDocError('')
    } catch (error) {
      const message = error?.response?.data?.message || 'Erreur chargement coach'
      openSnackbarRef.current(message)
      if (!keepExisting) {
        goBackToCoaches()
      }
    } finally {
      if (!keepExisting) setLoading(false)
    }
  }, [id, page, pageSize, goBackToCoaches])

  const refreshDocuments = useCallback(async () => {
    try {
      const { data: result } = await api.fetchCoachDocuments(id)
      setData((prev) => (prev ? { ...prev, documents: result.documents || [] } : prev))
      setUploadDocError('')
    } catch (error) {
      const message = error?.response?.data?.message || 'Impossible de recharger les documents'
      setUploadDocError(message)
    }
  }, [id])

  useEffect(() => {
    setPage(1)
  }, [pageSize])

  useEffect(() => {
    loadCoach()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, page, pageSize])

  const downloadPdf = async (invoiceId, campaignId, invoiceNumber) => {
    if (!campaignId) {
      openSnackbar('PDF non disponible')
      return
    }
    setPdfBusyInvoiceId(invoiceId)
    try {
      await downloadInvoicePdf(campaignId, invoiceId, invoiceNumber)
    } catch {
      openSnackbar('Erreur téléchargement PDF')
    } finally {
      setPdfBusyInvoiceId(null)
    }
  }

  const printPdf = async (invoiceId, campaignId) => {
    if (!campaignId) {
      openSnackbar('PDF non disponible')
      return
    }
    setPdfBusyInvoiceId(invoiceId)
    try {
      await printInvoicePdf(campaignId, invoiceId)
    } catch {
      openSnackbar('Erreur impression PDF')
    } finally {
      setPdfBusyInvoiceId(null)
    }
  }

  const handleDeleteCoach = async () => {
    setDeleting(true)
    try {
      await api.deleteCoach(id)
      openSnackbarRef.current('Coach supprimé')
      goBackToCoaches()
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur suppression')
    } finally {
      setDeleting(false)
      setConfirmDelete(false)
    }
  }

  const openEdit = () => {
    if (!data?.coach) return
    setForm(coachToForm(data.coach))
    setEditOpen(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) {
      openSnackbar('Nom requis')
      return
    }
    if (!form.matriculeFiscal.trim()) {
      openSnackbar('Matricule fiscal requis')
      return
    }
    setSaving(true)
    try {
      await api.updateCoach(id, form)
      openSnackbarRef.current('Coach mis à jour')
      setEditOpen(false)
      loadCoach()
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur enregistrement')
    } finally {
      setSaving(false)
    }
  }

  const uploadDocuments = async (files) => {
    if (!files?.length) return
    setUploadingDocs(true)
    setUploadDocError('')
    try {
      const formData = new FormData()
      files.forEach((file) => formData.append('files', file))
      if (docTypeUpload) formData.append('docType', docTypeUpload)
      const { data: result } = await api.uploadCoachDocuments(id, formData)
      const failed = (result.results || []).filter((row) => row.status === 'error')
      if (failed.length > 0) {
        setUploadDocError(failed.map((row) => `${row.fileName} : ${row.message}`).join(' · '))
      } else {
        openSnackbarRef.current(result.message || 'Documents enregistrés')
      }
      await refreshDocuments()
    } catch (error) {
      const message = error?.response?.data?.message
        || (error?.response?.status === 404
          ? 'Service documents indisponible — redémarrez le serveur ou déployez la dernière version'
          : 'Erreur upload documents')
      setUploadDocError(message)
      openSnackbarRef.current(message)
    } finally {
      setUploadingDocs(false)
    }
  }

  const onDropDocuments = useCallback((acceptedFiles) => {
    uploadDocuments(acceptedFiles)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, docTypeUpload])

  const onDropDocumentsRejected = useCallback((rejections) => {
    const firstError = rejections[0]?.errors?.[0]
    const message = firstError?.code === 'file-too-large'
      ? 'Fichier trop volumineux (maximum 50 Mo)'
      : (firstError?.message || 'Fichier non accepté (PDF ou image uniquement)')
    setUploadDocError(message)
  }, [])

  const { getRootProps: getDocRootProps, getInputProps: getDocInputProps, isDragActive: isDocDragActive } = useDropzone({
    onDrop: onDropDocuments,
    onDropRejected: onDropDocumentsRejected,
    accept: '.pdf,application/pdf,image/jpeg,image/png,image/webp',
    multiple: true,
    disabled: uploadingDocs,
    maxSize: 50 * 1024 * 1024,
  })

  const downloadDocument = async (docId, fileName) => {
    try {
      const response = await api.downloadCoachDocument(id, docId)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.download = fileName || 'document.pdf'
      link.click()
      window.URL.revokeObjectURL(url)
    } catch {
      openSnackbarRef.current('Erreur téléchargement document')
    }
  }

  const removeDocument = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return
    setDeletingDocId(docId)
    try {
      await api.deleteCoachDocument(id, docId)
      openSnackbarRef.current('Document supprimé')
      await refreshDocuments()
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur suppression')
    } finally {
      setDeletingDocId(null)
    }
  }

  if (loading && !data) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  if (!data?.coach) return null

  const { coach, invoices = [], documents = [], pagination } = data
  const totalInvoices = pagination?.total || 0
  const totalPages = pagination?.totalPages || 1

  return (
    <div className={styles.page}>
      <BackButton onClick={goBackToCoaches}>
        Retour aux coaches
      </BackButton>

      <div className={styles.profileCard}>
        <div className={styles.profileAvatar}>{coach.name?.charAt(0)?.toUpperCase()}</div>
        <div className={styles.profileInfo}>
          <h1>{coach.name}</h1>
          <div className={styles.profileMeta}>
            <span>MF : {coach.matriculeFiscal}</span>
            <span>{coach.email || 'Email non renseigné'}</span>
            <span>Tél. : {coach.phone || 'Non renseigné'}</span>
            <span>{documents.length} document(s) administratif(s)</span>
            <span>RIB : {coach.rib || 'Non renseigné'}</span>
            <span>ID Sage : {coach.idSage || 'Non renseigné'}</span>
            <span>{normalizeCoachTitre(coach.titre)}</span>
            <span>{coach.address || 'Adresse non renseignée'}</span>
            <span>
              Statut :{' '}
              {coach.isActive !== false ? (
                <span className={`${styles.dossierBadge} ${styles.statusBadgeActive}`}>Actif</span>
              ) : (
                <span className={`${styles.dossierBadge} ${styles.statusBadgeInactive}`}>Inactif</span>
              )}
            </span>
            <span>Catégorie fiscale : {formatFiscalCategoryLabel(coach.fiscalCategory)}</span>
            <span>
              Retenue par défaut :{' '}
              {isAutoEntrepreneur(coach) ? 'Non applicable' : formatRetenueLabel(coach.retenue)}
            </span>
          </div>
        </div>
        <div className={styles.profileActions}>
          <button
            type="button"
            className={styles.profileEditBtn}
            onClick={openEdit}
          >
            Modifier
          </button>
          {admin && (
            <button
              type="button"
              className={styles.profileDeleteBtn}
              disabled={deleting}
              onClick={() => setConfirmDelete(true)}
            >
              Supprimer
            </button>
          )}
        </div>
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Factures</span>
          <span className={styles.kpiValue}>{coach.invoiceCount || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Honoraires cumulés</span>
          <span className={styles.kpiValue}>{formatTND(coach.totalHonoraires)} TND</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Net cumulé</span>
          <span className={styles.kpiValue}>{formatTND(coach.totalNet)} TND</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Dernière campagne</span>
          <span className={styles.kpiValue} style={{ fontSize: '1.1rem' }}>
            {coach.periodLabel || '—'}
          </span>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Dossier administratif</h2>
            <p className={styles.hint}>
              Patente, CIN, RIB… Nommez vos fichiers : « {coach.name} - patente.pdf »
            </p>
          </div>
        </div>

        {uploadDocError && (
          <div className={styles.docUploadError} role="alert">
            {uploadDocError}
          </div>
        )}

        <div className={styles.docUploadBar}>
          <label htmlFor="coach-doc-type">Type (optionnel)</label>
          <select
            id="coach-doc-type"
            className={styles.docTypeSelect}
            value={docTypeUpload}
            onChange={(e) => setDocTypeUpload(e.target.value)}
            disabled={uploadingDocs}
          >
            {DOC_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value || 'auto'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div
          {...getDocRootProps()}
          className={`${styles.docUploadZone} ${isDocDragActive ? styles.stampUploadActive : ''}`}
        >
          <input {...getDocInputProps()} />
          <span className={styles.stampUploadIcon}>{isDocDragActive ? '📥' : '📁'}</span>
          <strong>
            {uploadingDocs
              ? 'Envoi en cours…'
              : 'Glisser des PDF/images ici ou cliquer pour ajouter au dossier'}
          </strong>
          <span className={styles.stampUploadHint}>PDF, JPG, PNG — plusieurs fichiers acceptés</span>
        </div>

        {documents.length === 0 ? (
          <p className={styles.empty}>Aucun document administratif pour ce coach.</p>
        ) : (
          <div className={`${styles.tableWrap} table-scroll-wrap`}>
            <table className={`${styles.table} responsive-table`}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Fichier</th>
                  <th>Taille</th>
                  <th>Ajouté le</th>
                  <th className={styles.docActionsCol}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc._id}>
                    <td data-label="Type">
                      <span className={styles.docTypeBadge}>{doc.label || doc.docType}</span>
                    </td>
                    <td data-label="Fichier">{doc.fileName}</td>
                    <td data-label="Taille">{formatFileSize(doc.fileSize)}</td>
                    <td data-label="Ajouté le">{formatDocDate(doc.createdAt)}</td>
                    <td className={styles.docActionsCol} data-label="Actions">
                      <div className={styles.docActionCell}>
                        <button
                          type="button"
                          className={styles.tableBtn}
                          onClick={() => downloadDocument(doc._id, doc.fileName)}
                        >
                          Télécharger
                        </button>
                        <button
                          type="button"
                          className={styles.deleteBtn}
                          disabled={deletingDocId === doc._id}
                          onClick={() => removeDocument(doc._id)}
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Historique des factures</h2>
            <p className={styles.hint}>{totalInvoices} facture(s) pour ce coach</p>
          </div>
        </div>

        {invoices.length === 0 ? (
          <p className={styles.empty}>Aucune facture enregistrée pour ce coach.</p>
        ) : (
          <div className={`${styles.tableWrap} table-scroll-wrap`}>
            <table className={`${styles.table} responsive-table`}>
              <thead>
                <tr>
                  <th>N° Facture</th>
                  <th>Période</th>
                  <th>Honoraires</th>
                  <th>Net à payer</th>
                  <th>Statut envoi</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const st = statusBadge(inv.emailStatus)
                  return (
                    <tr key={inv._id}>
                      <td data-label="N° Facture">{inv.invoiceNumber}</td>
                      <td data-label="Période">{formatPeriod(inv.periodMonth, inv.periodYear)}</td>
                      <td data-label="Honoraires">{formatTND(inv.honoraires)} TND</td>
                      <td data-label="Net à payer">{formatTND(inv.netAPayer)} TND</td>
                      <td data-label="Statut envoi"><span className={st.cls}>{st.text}</span></td>
                      <td data-label="Actions">
                        <button
                          type="button"
                          className={styles.tableBtn}
                          disabled={pdfBusyInvoiceId === inv._id}
                          onClick={() => downloadPdf(inv._id, inv.campaignId, inv.invoiceNumber)}
                        >
                          {pdfBusyInvoiceId === inv._id ? '…' : 'PDF'}
                        </button>
                        <button
                          type="button"
                          className={styles.tableBtn}
                          style={{ marginLeft: 8 }}
                          disabled={pdfBusyInvoiceId === inv._id}
                          onClick={() => printPdf(inv._id, inv.campaignId)}
                        >
                          {pdfBusyInvoiceId === inv._id ? '…' : 'Imprimer'}
                        </button>
                        {inv.campaignId && (
                          <button
                            type="button"
                            className={styles.tableBtn}
                            style={{ marginLeft: 8 }}
                            onClick={() => history.push(`/campagnes/${inv.campaignId}`)}
                          >
                            Campagne
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={page <= 1 || loading || totalInvoices === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Précédent
          </button>
          <span className={styles.pageLabel}>
            Page {totalInvoices === 0 ? 1 : (pagination?.page || page)} sur {totalPages}
            {' · '}{totalInvoices} facture(s)
          </span>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={page >= totalPages || loading || totalInvoices === 0}
            onClick={() => setPage((p) => p + 1)}
          >
            Suivant →
          </button>
          <select
            className={styles.pageSizeSelect}
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            aria-label="Nombre de factures par page"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>{size} / page</option>
            ))}
          </select>
        </div>
      </div>

      {editOpen && (
        <div className={styles.overlay} onClick={() => !saving && setEditOpen(false)} role="presentation">
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="coach-edit-title"
          >
            <h2 id="coach-edit-title">Modifier le coach</h2>
            <p className={styles.modalHint}>
              Les modifications sont appliquées à la fiche et aux factures existantes de ce coach.
            </p>
            <form onSubmit={handleSave}>
              <div className={styles.field}>
                <label htmlFor="coach-name">Nom complet *</label>
                <input
                  id="coach-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-mf">Matricule fiscal *</label>
                <input
                  id="coach-mf"
                  value={form.matriculeFiscal}
                  onChange={(e) => setForm({ ...form, matriculeFiscal: e.target.value })}
                  required
                  placeholder="12007658Y/A/P/000"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-email">Email</label>
                <input
                  id="coach-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="coach@california-gym.com"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-phone">Téléphone</label>
                <input
                  id="coach-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\s/g, '') })}
                  placeholder="Ex. 20123456"
                  inputMode="tel"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-rib">RIB</label>
                <input
                  id="coach-rib"
                  value={form.rib}
                  onChange={(e) => setForm({ ...form, rib: e.target.value.replace(/\s/g, '') })}
                  placeholder="20 chiffres"
                  inputMode="numeric"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-idsage">ID Sage</label>
                <input
                  id="coach-idsage"
                  value={form.idSage}
                  onChange={(e) => setForm({ ...form, idSage: e.target.value })}
                  placeholder="Identifiant Sage"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-titre">Titre / fonction</label>
                <input
                  id="coach-titre"
                  value={form.titre}
                  onChange={(e) => setForm({ ...form, titre: e.target.value })}
                  placeholder={COACH_TITRE}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="coach-address">Adresse</label>
                <textarea
                  id="coach-address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Adresse complète"
                  rows={3}
                />
              </div>
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label htmlFor="coach-fiscal">Catégorie fiscale</label>
                  <select
                    id="coach-fiscal"
                    value={form.fiscalCategory}
                    onChange={(e) => setForm(applyFiscalCategoryToForm(form, e.target.value))}
                  >
                    {FISCAL_CATEGORY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.field}>
                  <label htmlFor="coach-status">Statut</label>
                  <select
                    id="coach-status"
                    value={form.isActive ? 'active' : 'inactive'}
                    onChange={(e) => setForm({ ...form, isActive: e.target.value === 'active' })}
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
              </div>
              {!isAutoEntrepreneur(form) && (
                <div className={styles.field}>
                  <label htmlFor="coach-retenue">Retenue par défaut</label>
                  <select
                    id="coach-retenue"
                    value={form.retenue}
                    onChange={(e) => setForm({ ...form, retenue: Number(e.target.value) })}
                  >
                    {RETENUE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <StampUploader form={form} setForm={setForm} />
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => setEditOpen(false)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button type="submit" className={styles.saveBtn} disabled={saving}>
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirmDelete}
        variant="warning"
        title="Supprimer ce coach ?"
        message={`Vous allez supprimer la fiche de ${coach.name} du référentiel.`}
        detail={
          (coach.invoiceCount || 0) > 0
            ? `Les ${coach.invoiceCount} facture(s) restent dans leurs campagnes. Un prochain import peut recréer la fiche.`
            : 'Un prochain import Excel peut recréer automatiquement la fiche coach.'
        }
        confirmLabel={deleting ? 'Suppression…' : 'Supprimer'}
        cancelLabel="Annuler"
        loading={deleting}
        onConfirm={handleDeleteCoach}
        onCancel={() => !deleting && setConfirmDelete(false)}
      />
    </div>
  )
}

export default CoachDetail
